<template>
  <section class="space-y-4">
    <div v-if="isFareTypeSelected" class="flex items-center justify-between">
      <div class="text-situational-dark text-xs">{{ formTitle }}</div>
      <ResetButton @resetPassengerData="resetPassengerData" />
    </div>
    <FreeFormSegment
      v-else
      name="passenger-fare"
      :value="passenger.fareType"
      :items="fareOptions"
      :label="t('passengerDataForm.fare.label')"
      @onChange="updateFareType"
    />
    <div class="bg-background-neutral-secondary rounded-2xl p-4">
      <ResetButton
        v-if="!isFareTypeSelected"
        @resetPassengerData="resetPassengerData"
      />
      <div class="space-y-4">
        <DocumentTypeInput
          :document-type="passenger.documentType"
          :fare-type="passenger.fareType"
          @onDocumentTypeSelect="updateDocumentType"
        />
        <FreeFormInput
          ref="lastNameInput"
          v-maska:[inputsMasks.initials]
          :value="passenger.lastName"
          :label="t('passengerDataForm.lastName')"
          :has-error="validations.lastName.$error"
          :error-message="
            passengerDataValidation.lib.getErrorMessage(validations, 'lastName')
          "
          :placeholder="initialsPlaceholders.surname"
          enterkeyhint="next"
          @focus="validations.lastName.$reset()"
          @blur="validations.lastName.$touch()"
          @keydown="
            onKeyDown($event, () =>
              firstNameInput?.formInputRef?.inputRef?.focus(),
            )
          "
          @maska="updatePassenger({ lastName: $event.detail.masked })"
        />
        <FreeFormInput
          ref="firstNameInput"
          v-maska:[inputsMasks.initials]
          :value="passenger.firstName"
          :label="t('passengerDataForm.firstName')"
          :has-error="validations.firstName.$error"
          :error-message="
            passengerDataValidation.lib.getErrorMessage(
              validations,
              'firstName',
            )
          "
          :placeholder="initialsPlaceholders.name"
          enterkeyhint="next"
          @focus="validations.firstName.$reset()"
          @blur="validations.firstName.$touch()"
          @keydown="onKeyDown($event, firstNameKeyDown)"
          @maska="updatePassenger({ firstName: $event.detail.masked })"
        />
        <template v-if="isExtraDataRequired">
          <div class="space-y-2.5">
            <FreeFormInput
              ref="patronymicRef"
              v-maska:[inputsMasks.initials]
              :value="patronymicValue"
              :label="t('passengerDataForm.patronymic.label')"
              :has-error="validations.patronymic?.value.$error"
              :error-message="
                passengerDataValidation.lib.getErrorMessage(
                  validations,
                  'patronymic',
                )
              "
              :disabled="!passenger.patronymic.hasPatronymic"
              :placeholder="initialsPlaceholders.patronymic"
              enterkeyhint="next"
              @focus="validations.patronymic.$reset()"
              @blur="validations.patronymic.$touch()"
              @maska="
                updatePatronymic(
                  $event.detail.masked,
                  passenger.patronymic.hasPatronymic,
                )
              "
              @keydown="onKeyDown($event, patronymicKeyDown)"
            />
            <FormInputCheckbox
              :checked="!passenger.patronymic.hasPatronymic"
              @onChange="
                updatePatronymic(
                  passenger.patronymic.value,
                  !passenger.patronymic.hasPatronymic,
                )
              "
            >
              {{ t('passengerDataForm.patronymic.without') }}
            </FormInputCheckbox>
          </div>
          <CitizenshipInput
            ref="citizenshipRef"
            :citizenship="passenger.citizenship"
            :citizenship-bottom-sheet-name="citizenshipBottomSheetName"
            :has-error="validations.citizenship.$error"
            :error-message="
              passengerDataValidation.lib.getErrorMessage(
                validations,
                'citizenship',
              )
            "
            @on-citizenship-select="
              onCitizenshipSelect({ citizenship: $event })
            "
            @openCitizenshipBottomSheet="openCitizenshipBottomSheet"
          />
        </template>
        <FreeFormInput
          ref="documentNumberRef"
          v-maska:[documentNumberMask]
          :value="documentNumber"
          :label="t('passengerDataForm.document.type.default')"
          :has-error="validations.documentNumber.$error"
          :error-message="
            passengerDataValidation.lib.getErrorMessage(
              validations,
              'documentNumber',
            )
          "
          :placeholder="documentNumberLabels.placeholder"
          enterkeyhint="next"
          :type="isNumericDocumentNumber ? 'tel' : undefined"
          :inputmode="isNumericDocumentNumber ? 'numeric' : undefined"
          @maska="updatePassenger({ documentNumber: $event.detail.masked })"
          @focus="validations.documentNumber.$reset()"
          @blur="validations.documentNumber.$touch()"
          @keydown="onKeyDown($event, documentNumberKeyDown)"
        />
        <div class="space-y-2.5">
          <FreeFormInput
            ref="iinRef"
            v-maska:[inputsMasks.iin]
            type="tel"
            :value="iinValue"
            :label="t('passengerDataForm.iin.label')"
            :has-error="validations.iin?.value.$error"
            :error-message="validations.iin?.value?.$errors[0]?.$message"
            :disabled="!passenger.iin.hasIin"
            enterkeyhint="next"
            inputmode="numeric"
            placeholder="000000000000"
            @maska="updateIin($event.detail.masked, passenger.iin.hasIin)"
            @focus="validations.iin.$reset()"
            @blur="validations.iin.$touch()"
            @keydown="
              onKeyDown($event, () => iinRef?.formInputRef?.inputRef?.blur())
            "
          />
          <FormInputCheckbox
            v-if="!isIinRequired"
            :checked="!passenger.iin.hasIin"
            @onChange="updateIin(passenger.iin.value, !passenger.iin.hasIin)"
          >
            {{ t('passengerDataForm.iin.without') }}
          </FormInputCheckbox>
        </div>
        <FreeFormSegment
          v-if="shouldShowGender"
          ref="genderRef"
          name="passenger-gender"
          :value="passenger.gender"
          :items="genderOptions"
          :label="t('passengerDataForm.gender.label')"
          :has-error="validations.gender.$error"
          :error-message="
            passengerDataValidation.lib.getErrorMessage(validations, 'gender')
          "
          @onChange="updateGender"
        />
        <FreeFormInput
          v-if="shouldShowBirthDate"
          ref="dateOfBirthRef"
          v-maska:[inputsMasks.date]
          type="tel"
          :value="passenger.dateOfBirth"
          :label="t('passengerDataForm.dateOfBirth.label')"
          :has-error="validations.dateOfBirth.$error"
          :error-message="
            passengerDataValidation.lib.getErrorMessage(
              validations,
              'dateOfBirth',
            )
          "
          :placeholder="t('passengerDataForm.dateOfBirth.placeholder')"
          inputmode="numeric"
          @maska="updatePassenger({ dateOfBirth: $event.detail.masked })"
          @focus="validations.dateOfBirth.$reset()"
          @blur="validations.dateOfBirth.$touch()"
        />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  import { computed, useTemplateRef } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { railwaysMasksOptions } from 'freedom-shared';
  import { controllerModel } from 'shared';
  import { v4 as uuid } from 'uuid';

  import {
    rwPassengerDataValidation as passengerDataValidation,
    rwPassengerDataValidationTypes as passengerDataValidationTypes,
  } from '../../../features/rw-passenger-data-validation';
  import {
    rwPassengersData as passengersData,
    rwPassengersDataTypes as passengersDataTypes,
  } from '../../../entities/rw-passengers-data';
  import { FreeFormInput, FreeFormSegment } from '../../../shared/ui';
  import DocumentTypeInput from './DocumentTypeInput.vue';
  import FormInputCheckbox from './FormInputCheckbox.vue';
  import CitizenshipInput from './CitizenshipInput.vue';
  import { INITIALS } from '../config';
  import ResetButton from './ResetButton.vue';

  const NEXT_FOCUS_KEY = 'Enter';
  const NUMERIC_DOCUMENT_TYPES: passengersDataTypes.DocumentType[] = [
    passengersData.config.documentTypes.ID,
    passengersData.config.documentTypes.PASSPORT_KZ,
    passengersData.config.documentTypes.BIRTH_CERT,
  ];
  const LATIN_PLACEHOLDER_DOCUMENT_TYPES: passengersDataTypes.DocumentType[] = [
    ...passengerDataValidation.config.LATIN_ONLY_DOC_TYPES,
    passengersData.config.documentTypes.PASSPORT_KZ,
    passengersData.config.documentTypes.OTHER_INT_DOCUMENTS,
  ];

  const props = defineProps<{
    passenger: passengersDataTypes.PassengerData;
    validations: passengerDataValidationTypes.PassengerDataValidation;
    isExtraDataRequired: boolean;
    isFemaleCar?: boolean;
    isFareTypeSelected?: boolean;
    adultAge?: number;
  }>();

  const emit = defineEmits<{
    onPassengerUpdate: [passengersDataTypes.PassengerData];
  }>();

  const lastNameInput = useTemplateRef('lastNameInput');
  const firstNameInput = useTemplateRef('firstNameInput');
  const patronymicRef = useTemplateRef('patronymicRef');
  const genderRef = useTemplateRef('genderRef');
  const documentNumberRef = useTemplateRef('documentNumberRef');
  const citizenshipRef = useTemplateRef('citizenshipRef');
  const iinRef = useTemplateRef('iinRef');
  const dateOfBirthRef = useTemplateRef('dateOfBirthRef');

  const { t } = useI18n();

  const firstNameNextRef = computed(() => {
    if (!props.isExtraDataRequired) {
      return documentNumberRef.value;
    }
    return patronymicRef.value;
  });

  const formTitle = computed(() => {
    const { fareType } = props.passenger;
    const isChild = passengersData.lib.isChild(fareType);

    if (!isChild) {
      return t('passengerDataForm.fare.adult');
    }

    return t('passengerDataForm.fare.child', { age: props.adultAge });
  });

  const citizenshipBottomSheetName = `citizenship-bottom-sheet-${uuid()}`;
  const bottomSheet = controllerModel.useBottomSheet();

  const openCitizenshipBottomSheet = () =>
    bottomSheet.show(citizenshipBottomSheetName);

  const inputsMasks = railwaysMasksOptions;

  const documentNumberMask = computed(() => {
    return railwaysMasksOptions.getDocumentNumber(props.passenger.documentType);
  });

  const isPassportSelected = computed(() => {
    return passengersData.lib.isPassport(props.passenger.documentType);
  });

  const initialsPlaceholders = computed(() => {
    return LATIN_PLACEHOLDER_DOCUMENT_TYPES.includes(
      props.passenger.documentType,
    )
      ? INITIALS.en
      : INITIALS.ru;
  });

  const fareOptions = [
    {
      value: passengersDataTypes.FareType.ADULT,
      label: t('passengerDataForm.fare.adult'),
    },
    {
      value: passengersDataTypes.FareType.CHILD,
      label: t('passengerDataForm.fare.child'),
    },
  ];
  const genderOptions = [
    {
      value: passengersDataTypes.Gender.F,
      label: t('passengerDataForm.gender.female'),
    },
    {
      value: passengersDataTypes.Gender.M,
      label: t('passengerDataForm.gender.male'),
    },
  ];

  const isIinRequired = computed(() =>
    passengersData.lib.isIinRequired(props.passenger.documentType),
  );

  const iinValue = computed(() => {
    const { value, hasIin } = props.passenger.iin;
    return hasIin ? value : '';
  });

  const patronymicValue = computed(() => {
    const { patronymic } = props.passenger;
    return patronymic.hasPatronymic ? patronymic.value : '';
  });

  const isNumericDocumentNumber = computed(() =>
    NUMERIC_DOCUMENT_TYPES.includes(props.passenger.documentType),
  );

  const documentNumber = computed(() => {
    if (isPassportSelected.value && props.passenger.documentNumber[0] !== 'N') {
      const formattedDocumentNumber = `N${props.passenger.documentNumber}`;
      updatePassenger({ documentNumber: formattedDocumentNumber });
      return formattedDocumentNumber;
    }
    return props.passenger.documentNumber;
  });

  /** We need to show date of birth and gender inputs for children */
  const shouldShowBirthDate = computed(() => {
    return (
      passengersData.lib.isChild(props.passenger.fareType) ||
      props.isExtraDataRequired
    );
  });

  const shouldShowGender = computed(
    () => props.isExtraDataRequired || props.isFemaleCar,
  );

  /**
   * We only need to display label for known and popular documents.
   * For others doc types we display default text
   */
  const documentNumberLabels = computed(() => {
    switch (props.passenger.documentType) {
      case passengersData.config.documentTypes.ID:
      case passengersData.config.documentTypes.BIRTH_CERT:
      case passengersData.config.documentTypes.PASSPORT_KZ:
        return {
          placeholder: t(
            `passengerDataForm.document.placeholder.${props.passenger.documentType}`,
          ),
        };
      default:
        return {
          placeholder: t('passengerDataForm.document.placeholder.default'),
        };
    }
  });

  function updatePassenger(
    updatedData: Partial<passengersDataTypes.PassengerData>,
  ) {
    emit('onPassengerUpdate', { ...props.passenger, ...updatedData });
  }

  function onCitizenshipSelect(
    updatedData: Partial<passengersDataTypes.PassengerData>,
  ) {
    updatePassenger(updatedData);
    documentNumberRef.value?.formInputRef?.inputRef?.focus();
  }

  const updateFareType = (fareType: string) =>
    updatePassenger({ fareType: fareType as passengersDataTypes.FareType });
  const updateGender = (gender: string) => {
    updatePassenger({ gender: gender as passengersDataTypes.Gender });
    dateOfBirthRef.value?.formInputRef?.inputRef?.focus();
  };
  const updatePatronymic = (value: string, hasPatronymic: boolean) => {
    updatePassenger({
      patronymic: { value, hasPatronymic },
    });
  };

  const updateIin = (value: string, hasIin: boolean) => {
    const { gender, dateOfBirth } = passengersData.lib.parseIin(value);
    const updatedFields: Partial<passengersDataTypes.PassengerData> = {
      iin: { value, hasIin },
    };

    if (dateOfBirth && props.passenger.dateOfBirth.trim().length === 0) {
      updatedFields.dateOfBirth = dateOfBirth;
    }

    if (gender && props.passenger.gender.trim().length === 0) {
      updatedFields.gender = gender;
    }

    updatePassenger(updatedFields);
  };

  const updateDocumentType = (
    documentType: passengersDataTypes.DocumentType,
  ) => {
    if (props.passenger.documentType !== documentType) {
      const payload: Partial<passengersDataTypes.PassengerData> = {
        documentType,
        documentNumber: '',
      };
      /** In case if hasIin was set to false and IIN input was disabled */
      if (passengersData.lib.isIinRequired(documentType)) {
        /**
         * Sending as one event to prevent bugs with keeping old values.
         * If try use "updateIin" separately, it may rewrite document type with previous value.
         * It is related to destructure in draft state implementation.
         * */
        payload.iin = {
          value: iinValue.value,
          hasIin: true,
        };
      }
      if (documentType === passengersData.config.documentTypes.PASSPORT_KZ) {
        payload.patronymic = {
          value: patronymicValue.value,
          hasPatronymic: false,
        };
      } else {
        payload.patronymic = {
          value: patronymicValue.value,
          hasPatronymic: true,
        };
      }
      updatePassenger(payload);
      lastNameInput.value?.formInputRef?.inputRef?.focus();
    }
  };

  function onKeyDown(event: KeyboardEvent, callback: () => void) {
    if (event.key === NEXT_FOCUS_KEY) {
      callback();
    }
  }

  const firstNameKeyDown = () => {
    if (
      props.isExtraDataRequired &&
      !props.passenger.patronymic.hasPatronymic
    ) {
      firstNameInput.value?.formInputRef?.inputRef?.blur();
      openCitizenshipBottomSheet();
    } else {
      firstNameNextRef.value?.formInputRef?.inputRef?.focus();
    }
  };

  const patronymicKeyDown = () => {
    patronymicRef.value?.formInputRef?.inputRef?.blur();
    openCitizenshipBottomSheet();
  };

  const documentNumberKeyDown = () => {
    if (isIinRequired.value || props.passenger.iin.hasIin) {
      iinRef.value?.formInputRef?.inputRef?.focus();
      return;
    }
    documentNumberRef.value?.formInputRef?.inputRef?.blur();
  };

  function resetPassengerData() {
    updatePassenger({
      ...passengersData.config.defaultPassengerData,
      id: props.passenger.id,
      account: props.passenger.account,
      direction: props.passenger.direction,
      fareType: props.passenger.fareType,
      documentType: passengersData.lib.isChild(props.passenger.fareType)
        ? passengersData.config.documentTypes.BIRTH_CERT
        : passengersData.config.defaultPassengerData.documentType,
    });
  }
</script>

<i18n src="../config/locales.json"></i18n>
